import { TreeNodeInfo } from '@blueprintjs/core';
import { createSelector } from '@reduxjs/toolkit';
import { alphaSort } from 'vet-bones/utils';

import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getMappingTargetIri } from 'src/containers/ProjectDiagram/utils/mapping/getMappingTargetIri';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataMatchSelector } from 'src/selectors/projectLinkDataMatch';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';
import { projectResourcesByLabelSelector } from 'src/selectors/projectResource';
import { filterEmptyNodes } from 'src/utils/tree';

export type ProjectMappingTreeNodeData = {
  mappingId?: string;
};

export const projectMappingResourcesTreeSelector = (
  projectId: string,
  expandedNodeIds: Record<string, boolean> = {},
  selectedMappingId?: string
) =>
  createSelector(
    projectLabelsByIriSelector(projectId),
    projectLinkDataMatchSelector(projectId, { category: LinkCategory.MAPPING }),
    projectResourcesByLabelSelector(projectId),
    projectNodeDataArraySelector(projectId),
    (labelsByIri, linkData, resources, nodeDataArray) => {
      const resourcesTree: Array<TreeNodeInfo<ProjectMappingTreeNodeData>> =
        resources
          .map((resource) => {
            const mappingsByModelConcept: Record<
              string,
              Array<{ label: string; mappingId: string }>
            > = {};
            const resourceKey = getResourceKey(resource.id);
            (linkData as Mapping[]).forEach((link) => {
              if (link.from === resourceKey && link.to) {
                const iri = getMappingTargetIri(link.to, nodeDataArray);
                if (!iri) {
                  return;
                }

                if (!mappingsByModelConcept[iri]) {
                  mappingsByModelConcept[iri] = [];
                }
                mappingsByModelConcept[iri].push({
                  label: link.text,
                  mappingId: link.value,
                });
              }
            });

            return {
              // Level 1: Resources
              id: resource.id,
              childNodes: Object.entries(mappingsByModelConcept)
                .map(([modelConceptIri, mappings]): TreeNodeInfo => {
                  // Level 2: Model Concepts
                  const modelConceptNodeId = `${resource.id}-${modelConceptIri}`;
                  return {
                    id: modelConceptNodeId,
                    childNodes: mappings
                      .map(({ label, mappingId }) => {
                        // Level 3: Mappings
                        const id = `${modelConceptNodeId}-${mappingId}`;
                        const isSelected = selectedMappingId === mappingId;
                        return {
                          id,
                          isSelected,
                          label,
                          nodeData: { mappingId },
                        };
                      })
                      .sort(alphaSort(({ label }) => label)),
                    hasCaret: true,
                    isExpanded: expandedNodeIds[modelConceptNodeId] ?? false,
                    label: getLabelForIri(labelsByIri, modelConceptIri),
                  };
                })
                .filter(filterEmptyNodes)
                .sort(alphaSort((item) => item.label as string)),
              hasCaret: true,
              isExpanded: expandedNodeIds[resource.id] ?? false,
              label: resource.label,
            };
          })
          .filter(filterEmptyNodes);

      return resourcesTree;
    }
  );
